// src/pages/BotDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Textarea,
  Checkbox,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Switch,
  Grid,
  GridItem,
  Badge,
  IconButton,
  useColorModeValue,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  useToast,
  Divider,
  SimpleGrid,
  Flex,
  Spacer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Container,
  Icon,
  Progress
} from '@chakra-ui/react';
import {
  EditIcon,
  DeleteIcon,
  DownloadIcon,
  AddIcon,
  CheckIcon,
  CloseIcon,
  CopyIcon,
  ExternalLinkIcon
} from '@chakra-ui/icons';
import { FiUpload, FiFile, FiFileText, FiSettings, FiLink, FiTrash2 } from 'react-icons/fi';
import ContactsTab from '../components/bots/ContactsTab';
import * as apiService from '../services/api';
import './BotDetail.css';


const BotDetail = () => {
  const { botId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  
  // Enhanced theme colors with proper dark/light mode support
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const textTertiary = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.700');
  const readOnlyBg = useColorModeValue('gray.50', 'gray.600');
  const dragAreaBg = useColorModeValue('gray.50', 'gray.700');
  const dragAreaActiveBg = useColorModeValue('blue.50', 'blue.900');
  const modalBg = useColorModeValue('white', 'gray.800');
  const modalOverlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800');
  
  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [botData, setBotData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [availableConnections, setAvailableConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connectionFormData, setConnectionFormData] = useState({});
  const [providerTemplate, setProviderTemplate] = useState({});
  const { isOpen: isConnectionModalOpen, onOpen: onConnectionModalOpen, onClose: onConnectionModalClose } = useDisclosure();

  // Connection modal handlers
  const openEditConnectionModal = (connection) => {
    setSelectedConnection(connection);

    connection = availableConnections.filter(conn => conn.id === connection.id)[0] || connection;
    let template = {};
    try {
      console.log(connection);
      console.log(connection.provider_template);
      const validJson = connection.provider_template
        .replace(/'/g, '"')
        .trim();
      template = JSON.parse(validJson);
    } catch (e) {
      console.error('Error parsing provider template:', e);
      template = connection.provider_template;
    }
    console.log(template);
    setProviderTemplate(template);
    
    let connectionData = {};
    try {
      connectionData = connection;
    } catch (e) {
      console.error('Error parsing connection data:', e);
    }
    
    const initialFormData = {};
    Object.keys(template).forEach(key => {
      initialFormData[key] = connectionData[key] || template[key] || '';
    });
    
    setConnectionFormData(initialFormData);
    onConnectionModalOpen();
  };

  const handleConnectionInputChange = (field, value) => {
    setConnectionFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConnectionSave = async () => {
    try {
      setIsLoading(true);
      const ui = localStorage.getItem('user_info');
      const email = JSON.parse(ui).email;
      
      const response = await fetch(
        '85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/connection',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            id: selectedConnection.id,
            connection_data: connectionFormData
          })
        }
      );

      const data = await response.json();
      
      if (data.status === 'success') {
        setAvailableConnections(prev => 
          prev.map(conn => 
            conn.id === selectedConnection.id 
              ? { ...conn, ...data.connection }
              : conn
          )
        );
        
        toast({
          title: 'Success',
          description: 'Connection credentials updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        onConnectionModalClose();
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to update connection credentials',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating connection:', error);
      toast({
        title: 'Error',
        description: 'Network error. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const closeConnectionModal = () => {
    onConnectionModalClose();
    setSelectedConnection(null);
    setConnectionFormData({});
  };

  // Load bot data and connections
  useEffect(() => {
    loadBotData();
  }, [botId]);

  const loadBotData = () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user_info'));
      const storedBots = userInfo.bots;
      if (storedBots) {
        const parsedBots = storedBots;
        const currentBot = parsedBots.find(bot => bot.id === parseInt(botId));
        
        if (currentBot) {
          setBotData(currentBot);
          setAttachments(currentBot.attachments || []);
          setAvailableConnections(currentBot.connections);
          return;
        }
      }
      
      // Fallback mock data
      const mockBot = {
        id: parseInt(botId),
        name: "Asistente de Ventas",
        description: "Bot diseñado para asistir en consultas sobre productos y realizar ventas automatizadas.",
        n8n_workflow_id: "wf_" + botId + "_sales_assistant",
        active: true,
        provider_ids: [1, 3],
        connection_ids: [2, 5],
        attachment_ids: [101, 102],
        exclude_list: "+34612345678, cliente@ejemplo.com",
        exclude_criteria: "fuera_de_horario, vacaciones",
        process_attachments: true,
        make_calls: false,
        system_prompt: "Eres un asistente de ventas amable y profesional.",
        has_multiple_triggers: true,
        trigger_count: 2,
        options: [],
        rubrica: null
      };
      
      setBotData(mockBot);
    } catch (error) {
      console.error('Error loading bot data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load bot data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Event handlers
  const handleInputChange = (field, value) => {
    setBotData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      const ui = localStorage.getItem('user_info');
      const email = JSON.parse(ui).email;
      
      const response = await fetch(
        `85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/bots/${botId}/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            ...botData
          })
        }
      );
      
      const data = await response.json();
      
      if (data.status === 'success') {
        toast({
          title: 'Success',
          description: data.message || 'Bot updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setIsEditing(false);
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to update bot',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating bot:', error);
      toast({
        title: 'Error',
        description: 'Network error. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadBotData();
  };

  const handleBotUpdate = () => {
    loadBotData();
  };

  // File upload handlers
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsLoading(true);
    const formData = new FormData();
    
    try {
      formData.append('bot_id', botId);
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await fetch(`85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/bots/${botId}/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        const newAttachments = result.attachments.map(att => ({
          id: att.id,
          name: att.name,
          type: att.mimetype,
          size: formatFileSize(att.file_size),
          date: new Date(att.create_date).toLocaleDateString(),
          url: att.url
        }));
        
        setAttachments(prev => [...prev, ...newAttachments]);
        
        toast({
          title: 'Success',
          description: `${result.attachments.length} file(s) uploaded successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to upload files',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const renderFormField = (key, value, template) => {
    const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const isPassword = key.toLowerCase().includes('password') || key.toLowerCase().includes('secret');
    const isNumber = key.toLowerCase().includes('port') || typeof template[key] === 'number';
    
    return (
      <FormControl key={key} mb={4}>
        <FormLabel fontSize="sm" fontWeight="600" color={textSecondary}>
          {fieldName}
        </FormLabel>
        <Input
          type={isPassword ? 'password' : isNumber ? 'number' : 'text'}
          placeholder={`Enter ${fieldName}`}
          value={value || ''}
          onChange={(e) => handleConnectionInputChange(key, e.target.value)}
          bg={inputBg}
          borderColor={borderColor}
          color={textPrimary}
          borderRadius="lg"
          _focus={{
            borderColor: 'blue.400',
            boxShadow: '0 0 0 1px #3182ce'
          }}
          _placeholder={{ color: textTertiary }}
        />
      </FormControl>
    );
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const ui = localStorage.getItem('user_info');
      const email = JSON.parse(ui).email;
      const response = await fetch(
        `85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/bots/${botId}/attachment_delete/${attachmentId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 'email': email })
        }
      );

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
        
        toast({
          title: 'Success',
          description: data.message || 'Attachment deleted successfully',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to delete attachment',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast({
        title: 'Error',
        description: 'Network error. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (!botData) {
    return (
      <Box bg={bg} minH="100vh" p={6}>
        <Center h="50vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color={textSecondary} fontSize="lg">Loading bot details...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box bg={bg} minH="100vh" p={6}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* Enhanced Header */}
          <Card  shadow="xl" borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
            <CardHeader p={8}>
              <Flex align="center">
                <VStack align="start" spacing={2}>
                  <Heading size="xl" color={textPrimary} fontWeight="700">
                    {isEditing ? 'Edit Bot' : 'Bot Details'}
                  </Heading>
                  <Text color={textSecondary} fontSize="md">
                    Manage your bot configuration and settings
                  </Text>
                  <HStack spacing={4}>
                    <Badge 
                      colorScheme={botData?.active ? 'green' : 'red'} 
                      px={3} 
                      py={1} 
                      borderRadius="full"
                      fontWeight="600"
                    >
                      {botData?.active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Text fontSize="sm" color={textTertiary}>
                      ID: {botData?.id}
                    </Text>
                  </HStack>
                </VStack>
                <Spacer />
                <HStack spacing={3}>
                  {!isEditing ? (
                    <>
                      <Button
                        leftIcon={<EditIcon />}
                        colorScheme="blue"
                        size="lg"
                        borderRadius="xl"
                        onClick={() => setIsEditing(true)}
                        _hover={{ transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                      >
                        Edit
                      </Button>
                      <Button
                        leftIcon={<ExternalLinkIcon />}
                        variant="outline"
                        size="lg"
                        borderRadius="xl"
                        borderColor={borderColor}
                        color={textPrimary}
                        onClick={() => navigate(`/flux/${botId}`)}
                        _hover={{ 
                          borderColor: 'purple.400',
                          color: 'purple.400',
                          transform: 'translateY(-2px)'
                        }}
                        transition="all 0.2s"
                      >
                        Edit Flow
                      </Button>
                      <Button 
                        leftIcon={<CopyIcon />} 
                        variant="outline"
                        size="lg"
                        borderRadius="xl"
                        borderColor={borderColor}
                        color={textPrimary}
                        _hover={{ 
                          borderColor: 'green.400',
                          color: 'green.400',
                          transform: 'translateY(-2px)'
                        }}
                        transition="all 0.2s"
                      >
                        Duplicate
                      </Button>
                      <Button 
                        leftIcon={<DeleteIcon />} 
                        colorScheme="red" 
                        variant="outline"
                        size="lg"
                        borderRadius="xl"
                        _hover={{ transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                      >
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        leftIcon={<CheckIcon />}
                        colorScheme="green"
                        size="lg"
                        borderRadius="xl"
                        onClick={handleSave}
                        isLoading={isLoading}
                        loadingText="Saving..."
                        _hover={{ transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                      >
                        Save Changes
                      </Button>
                      <Button
                        leftIcon={<CloseIcon />}
                        variant="outline"
                        size="lg"
                        borderRadius="xl"
                        borderColor={borderColor}
                        color={textPrimary}
                        onClick={handleCancel}
                        _hover={{ 
                          borderColor: 'red.400',
                          color: 'red.400',
                          transform: 'translateY(-2px)'
                        }}
                        transition="all 0.2s"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </HStack>
              </Flex>
            </CardHeader>
          </Card>

          {/* Enhanced Main Content */}
          <Card  shadow="xl" borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
            <CardBody p={0}>
              <Tabs variant="enclosed" colorScheme="blue" isLazy>
                <TabList px={8} className='tabrow' pt={6} borderColor={borderColor}>
                  <Tab 
                    className='tabbutton'
                    fontWeight="600" 
                    fontSize="md"
                    _selected={{ 
                      color: 'blue.500', 
                      borderColor: 'blue.500',
                      borderBottomWidth: '3px'
                    }}
                    _hover={{ color: 'blue.400' }}
                    transition="all 0.2s"
                  >
                    General
                  </Tab>
                  <Tab 
                    fontWeight="600" 
                    fontSize="md"
                    _selected={{ 
                      color: 'blue.500', 
                      borderColor: 'blue.500',
                      borderBottomWidth: '3px'
                    }}
                    _hover={{ color: 'blue.400' }}
                    transition="all 0.2s"
                  >
                    Connections
                  </Tab>
                  <Tab 
                    fontWeight="600" 
                    fontSize="md"
                    _selected={{ 
                      color: 'blue.500', 
                      borderColor: 'blue.500',
                      borderBottomWidth: '3px'
                    }}
                    _hover={{ color: 'blue.400' }}
                    transition="all 0.2s"
                  >
                    Contacts
                  </Tab>
                  <Tab 
                    fontWeight="600" 
                    fontSize="md"
                    _selected={{ 
                      color: 'blue.500', 
                      borderColor: 'blue.500',
                      borderBottomWidth: '3px'
                    }}
                    _hover={{ color: 'blue.400' }}
                    transition="all 0.2s"
                  >
                    Attachments
                  </Tab>
                  <Tab 
                    fontWeight="600" 
                    fontSize="md"
                    _selected={{ 
                      color: 'blue.500', 
                      borderColor: 'blue.500',
                      borderBottomWidth: '3px'
                    }}
                    _hover={{ color: 'blue.400' }}
                    transition="all 0.2s"
                  >
                    Options
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Enhanced General Tab */}
                  <TabPanel p={8}>
                    <VStack spacing={8} align="stretch">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                        <FormControl>
                          <FormLabel fontWeight="600" color={textSecondary} mb={3}>
                            Bot Name
                          </FormLabel>
                          <Input
                            defaultValue={botData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            isReadOnly={!isEditing}
                            bg={!isEditing ? readOnlyBg : inputBg}
                            borderColor={borderColor}
                            color={textPrimary}
                            borderRadius="lg"
                            size="lg"
                            _focus={{
                              borderColor: 'blue.400',
                              boxShadow: '0 0 0 1px #3182ce'
                            }}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="600" color={textSecondary} mb={3}>
                            N8N Workflow ID
                          </FormLabel>
                          <Input
                            defaultValue={botData.n8n_workflow_id}
                            onChange={(e) => handleInputChange('n8n_workflow_id', e.target.value)}
                            isReadOnly={!isEditing}
                            bg={!isEditing ? readOnlyBg : inputBg}
                            borderColor={borderColor}
                            color={textPrimary}
                            borderRadius="lg"
                            size="lg"
                            _focus={{
                              borderColor: 'blue.400',
                              boxShadow: '0 0 0 1px #3182ce'
                            }}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={8}>
                        <Card 
                          variant="outline" 
                          borderColor={borderColor} 
                          borderRadius="xl"
                          p={4}
                        >
                          <FormControl display="flex" alignItems="center" justifyContent="space-between">
                            <FormLabel mb="0" fontWeight="600" color={textPrimary}>
                              Bot Active
                            </FormLabel>
                            <Switch
                              isChecked={botData.active}
                              onChange={(e) => handleInputChange('active', e.target.checked)}
                              isDisabled={!isEditing}
                              colorScheme="blue"
                              size="lg"
                            />
                          </FormControl>
                        </Card>

                        <Card 
                          variant="outline" 
                          borderColor={borderColor} 
                          borderRadius="xl"
                          p={4}
                        >
                          <FormControl display="flex" alignItems="center" justifyContent="space-between">
                            <FormLabel mb="0" fontWeight="600" color={textPrimary}>
                              Multiple Triggers
                            </FormLabel>
                            <Switch
                              isChecked={botData.has_multiple_triggers}
                              onChange={(e) => handleInputChange('has_multiple_triggers', e.target.checked)}
                              isDisabled={!isEditing}
                              colorScheme="blue"
                              size="lg"
                            />
                          </FormControl>
                        </Card>

                        <Card 
                          variant="outline" 
                          borderColor={borderColor} 
                          borderRadius="xl"
                          p={4}
                        >
                          <FormControl display="flex" alignItems="center" justifyContent="space-between">
                            <FormLabel mb="0" fontWeight="600" color={textPrimary}>
                              Process Attachments
                            </FormLabel>
                            <Switch
                              isChecked={botData.process_attachments}
                              onChange={(e) => handleInputChange('process_attachments', e.target.checked)}
                              isDisabled={!isEditing}
                              colorScheme="blue"
                              size="lg"
                            />
                          </FormControl>
                        </Card>

                        <Card 
                          variant="outline" 
                          borderColor={borderColor} 
                          borderRadius="xl"
                          p={4}
                        >
                          <FormControl display="flex" alignItems="center" justifyContent="space-between">
                            <FormLabel mb="0" fontWeight="600" color={textPrimary}>
                              Make Calls
                            </FormLabel>
                            <Switch
                              isChecked={botData.make_calls}
                              onChange={(e) => handleInputChange('make_calls', e.target.checked)}
                              isDisabled={!isEditing}
                              colorScheme="blue"
                              size="lg"
                            />
                          </FormControl>
                        </Card>
                      </SimpleGrid>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                        <FormControl>
                          <FormLabel fontWeight="600" color={textSecondary} mb={3}>
                            Exclude Numbers/Emails
                          </FormLabel>
                          <Textarea
                            defaultValue={botData.exclude_list}
                            onChange={(e) => handleInputChange('exclude_list', e.target.value)}
                            isReadOnly={!isEditing}
                            bg={!isEditing ? readOnlyBg : inputBg}
                            borderColor={borderColor}
                            color={textPrimary}
                            borderRadius="lg"
                            resize="vertical"
                            minH="120px"
                            placeholder="Enter numbers or emails to exclude"
                            _placeholder={{ color: textTertiary }}
                            _focus={{
                              borderColor: 'blue.400',
                              boxShadow: '0 0 0 1px #3182ce'
                            }}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="600" color={textSecondary} mb={3}>
                            Exclude Criteria
                          </FormLabel>
                          <Textarea
                            defaultValue={botData.exclude_criteria}
                            onChange={(e) => handleInputChange('exclude_criteria', e.target.value)}
                            isReadOnly={!isEditing}
                            bg={!isEditing ? readOnlyBg : inputBg}
                            borderColor={borderColor}
                            color={textPrimary}
                            borderRadius="lg"
                            resize="vertical"
                            minH="120px"
                            placeholder="Enter exclusion criteria"
                            _placeholder={{ color: textTertiary }}
                            _focus={{
                              borderColor: 'blue.400',
                              boxShadow: '0 0 0 1px #3182ce'
                            }}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <FormControl>
                        <FormLabel fontWeight="600" color={textSecondary} mb={3}>
                          System Prompt
                        </FormLabel>
                        <Textarea
                          defaultValue={botData.system_prompt}
                          onChange={(e) => handleInputChange('system_prompt', e.target.value)}
                          isReadOnly={!isEditing}
                          bg={!isEditing ? readOnlyBg : inputBg}
                          borderColor={borderColor}
                          color={textPrimary}
                          borderRadius="lg"
                          resize="vertical"
                          minH="150px"
                          placeholder="Enter the system prompt for your bot"
                          _placeholder={{ color: textTertiary }}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px #3182ce'
                          }}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontWeight="600" color={textSecondary} mb={3}>
                          Description
                        </FormLabel>
                        <Textarea
                          defaultValue={botData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          isReadOnly={!isEditing}
                          bg={!isEditing ? readOnlyBg : inputBg}
                          borderColor={borderColor}
                          color={textPrimary}
                          borderRadius="lg"
                          resize="vertical"
                          minH="120px"
                          placeholder="Describe what this bot does"
                          _placeholder={{ color: textTertiary }}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px #3182ce'
                          }}
                        />
                      </FormControl>
                    </VStack>
                  </TabPanel>

                  {/* Enhanced Connections Tab */}
                  <TabPanel p={8}>
                    <VStack spacing={8} align="stretch">
                      <Box>
                        <Heading size="lg" mb={3} color={textPrimary} fontWeight="700">
                          Bot Connections
                        </Heading>
                        <Text color={textSecondary} fontSize="md">
                          Select the connections this bot can use and configure their credentials
                        </Text>
                      </Box>

                      {availableConnections?.length === 0 ? (
                        <Alert 
                          status="info" 
                          borderRadius="xl" 
                          bg={bg}
                          borderColor={borderColor}
                          borderWidth="1px"
                        >
                          <AlertIcon color="blue.500" />
                          <VStack align="start" spacing={3}>
                            <Text fontWeight="600" color={textPrimary}>
                              No connections available
                            </Text>
                            <Text fontSize="sm" color={textSecondary}>
                              Add connections first to enable your bot to interact with external services.
                            </Text>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              borderRadius="lg"
                              onClick={() => navigate('/connections')}
                              _hover={{ transform: 'translateY(-1px)' }}
                              transition="all 0.2s"
                            >
                              Go to Connections
                            </Button>
                          </VStack>
                        </Alert>
                      ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                          {availableConnections.map(connection => (
                            <Card
                              key={connection.id}
                              variant="outline"
                              style={{maxWidth: '360px', position: 'relative', border: '1px solid green', borderRadius: '20px'}}
                              cursor={isEditing ? "pointer" : "default"}
                              _hover={isEditing ? { 
                                borderColor: "blue.300",
                                transform: 'translateY(-2px)',
                                shadow: 'lg'
                              } : {}}
                              transition="all 0.2s"
                            >
                              <CardBody p={6}>
                                <VStack align="start" spacing={4}>
                                  <HStack w="full" justify="space-between">
                                    <Checkbox
                                      isChecked={botData.connection_ids?.includes(connection.id)}
                                      onChange={() => {
                                        if (isEditing) {
                                          const currentConnections = [...(botData.connection_ids || [])];
                                          if (currentConnections.includes(connection.id)) {
                                            setBotData(prev => ({
                                              ...prev,
                                              connection_ids: currentConnections.filter(id => id !== connection.id)
                                            }));
                                          } else {
                                            setBotData(prev => ({
                                              ...prev,
                                              connection_ids: [...currentConnections, connection.id]
                                            }));
                                          }
                                        }
                                      }}
                                      isDisabled={!isEditing}
                                      colorScheme="blue"
                                      size="lg"
                                    />
                                    <Badge 
                                      colorScheme={connection.active ? "green" : "red"}
                                      borderRadius="full"
                                      px={3}
                                      py={1}
                                      fontSize="xs"
                                      fontWeight="600"
                                    >
                                      {connection.active ? "Active" : "Inactive"}
                                    </Badge>
                                  </HStack>
                                  
                                  <Box>
                                    <Text fontWeight="700" fontSize="lg" color={textPrimary} mb={2}>
                                      {connection.name}
                                    </Text>
                                    <VStack align="start" spacing={2} fontSize="sm">
                                      <HStack>
                                        <Icon as={FiLink} color="blue.400" />
                                        <Text color={textSecondary}>
                                          <Text as="span" fontWeight="600">Provider:</Text> {connection.provider_name || connection.provider}
                                        </Text>
                                      </HStack>
                                    </VStack>
                                  </Box>
                                  
                                  <HStack spacing={3} w="full">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      colorScheme="blue"
                                      borderRadius="lg"
                                      onClick={() => openEditConnectionModal(connection)}
                                      leftIcon={<Icon as={FiSettings} />}
                                      _hover={{ transform: 'translateY(-1px)' }}
                                      transition="all 0.2s"
                                      flex={1}
                                    >
                                      Configure
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      colorScheme="green"
                                      borderRadius="lg"
                                      isDisabled={!botData.connection_ids?.includes(connection.id)}
                                      _hover={{ transform: 'translateY(-1px)' }}
                                      transition="all 0.2s"
                                      flex={1}
                                    >
                                      Test
                                    </Button>
                                  </HStack>
                                </VStack>
                              </CardBody>
                            </Card>
                          ))}
                        </SimpleGrid>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Contacts Tab */}
                  <TabPanel p={8}>
                    <ContactsTab 
                      bot={botData} 
                      onUpdate={handleBotUpdate}
                      loading={isLoading}
                     
                    />
                  </TabPanel>

                  {/* Enhanced Attachments Tab */}
                  <TabPanel p={8}>
                    <VStack spacing={8} align="stretch">
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Heading size="lg" mb={2} color={textPrimary} fontWeight="700">
                            File Attachments
                          </Heading>
                          <Text color={textSecondary} fontSize="md">
                            Manage files associated with this bot
                          </Text>
                        </Box>
                        {isEditing && (
                          <Button
                            leftIcon={<Icon as={FiUpload} />}
                            colorScheme="blue"
                            size="lg"
                            borderRadius="xl"
                            onClick={() => fileInputRef.current?.click()}
                            _hover={{ transform: 'translateY(-2px)' }}
                            transition="all 0.2s"
                          >
                            Upload Files
                          </Button>
                        )}
                      </Flex>

                      <Box
                        border="2px dashed"
                        borderColor={isDragging ? "blue.400" : borderColor}
                        borderRadius="xl"
                        p={12}
                        textAlign="center"
                        cursor="pointer"
                        bg={isDragging ? dragAreaActiveBg : dragAreaBg}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        transition="all 0.2s"
                        _hover={{ borderColor: 'blue.300' }}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                        />
                        {isLoading ? (
                          <VStack spacing={4}>
                            <Progress value={75} colorScheme="blue" size="lg" w="200px" borderRadius="full" />
                            <Text color={textSecondary} fontWeight="600">Uploading files...</Text>
                          </VStack>
                        ) : (
                          <VStack spacing={4}>
                            <Icon as={FiUpload} boxSize="64px" color={isDragging ? "blue.400" : "gray.400"} />
                            <VStack spacing={2}>
                              <Text fontWeight="700" fontSize="lg" color={textPrimary}>
                                Drag files here or click to upload
                              </Text>
                              <Text fontSize="md" color={textSecondary}>
                                Supported formats: PDF, DOCX, TXT, CSV, XLSX (max 10MB)
                              </Text>
                            </VStack>
                          </VStack>
                        )}
                      </Box>

                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                        {attachments.map(attachment => (
                          <Card 
                            key={attachment.id} 
                            variant="outline" 
                            borderColor={borderColor}
                            borderRadius="xl"
                            _hover={{ 
                              transform: 'translateY(-2px)',
                              shadow: 'lg'
                            }}
                            transition="all 0.2s"
                          >
                            <CardBody p={6}>
                              <VStack align="start" spacing={4}>
                                <HStack>
                                  <Icon
                                    as={attachment.type?.includes('pdf') ? FiFile : FiFileText}
                                    color={attachment.type?.includes('pdf') ? "red.500" : "blue.500"}
                                    boxSize="32px"
                                  />
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="700" noOfLines={1} color={textPrimary}>
                                      {attachment.name}
                                    </Text>
                                    <Text fontSize="sm" color={textTertiary}>
                                      {attachment.type}
                                    </Text>
                                  </VStack>
                                </HStack>
                                
                                <VStack align="start" spacing={1} fontSize="sm" w="full">
                                  <HStack justify="space-between" w="full">
                                    <Text color={textSecondary}>Size:</Text>
                                    <Text color={textPrimary} fontWeight="600">{attachment.size}</Text>
                                  </HStack>
                                  <HStack justify="space-between" w="full">
                                    <Text color={textSecondary}>Uploaded:</Text>
                                    <Text color={textPrimary} fontWeight="600">{attachment.date}</Text>
                                  </HStack>
                                </VStack>
                                
                                <HStack spacing={2} w="full">
                                  <Button 
                                    size="sm" 
                                    leftIcon={<DownloadIcon />} 
                                    variant="outline"
                                    borderRadius="lg"
                                    flex={1}
                                    _hover={{ transform: 'translateY(-1px)' }}
                                    transition="all 0.2s"
                                  >
                                    Download
                                  </Button>
                                  <IconButton
                                    icon={<Icon as={FiTrash2} />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    borderRadius="lg"
                                    onClick={() => handleDeleteAttachment(attachment.id)}
                                    aria-label="Delete attachment"
                                    _hover={{ transform: 'translateY(-1px)' }}
                                    transition="all 0.2s"
                                  />
                                </HStack>
                              </VStack>
                            </CardBody>
                          </Card>
                        ))}
                      </SimpleGrid>

                      {attachments.length === 0 && (
                        <Center py={12}>
                          <VStack spacing={4}>
                            <Icon as={FiFile} boxSize="64px" color="gray.400" />
                            <VStack spacing={2}>
                              <Text color={textSecondary} fontSize="lg" fontWeight="600">
                                No attachments yet
                              </Text>
                              {isEditing && (
                                <Text fontSize="md" color={textTertiary}>
                                  Upload files to provide context for your bot
                                </Text>
                              )}
                            </VStack>
                          </VStack>
                        </Center>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Enhanced Options Tab */}
                  <TabPanel p={8}>
                    <VStack spacing={8} align="stretch">
                      <Box>
                        <Heading size="lg" mb={2} color={textPrimary} fontWeight="700">
                          Configuration Options
                        </Heading>
                        <Text color={textSecondary} fontSize="md">
                          Advanced settings and options for this bot
                        </Text>
                      </Box>

                      {!botData.options || botData.options.length === 0 ? (
                        <Center py={12}>
                          <VStack spacing={4}>
                            <Icon as={FiSettings} boxSize="64px" color="gray.400" />
                            <VStack spacing={2}>
                              <Text color={textSecondary} fontSize="lg" fontWeight="600">
                                No configuration options available
                              </Text>
                              <Text fontSize="md" color={textTertiary}>
                                This bot doesn't have any configurable options
                              </Text>
                            </VStack>
                          </VStack>
                        </Center>
                      ) : (
                        <Card 
                          variant="outline" 
                          borderColor={borderColor}
                          borderRadius="xl"
                        >
                          <TableContainer>
                            <Table variant="simple">
                              <Thead>
                                <Tr>
                                  <Th 
                                    color={textSecondary} 
                                    fontWeight="700" 
                                    fontSize="sm"
                                    textTransform="none"
                                  >
                                    Option
                                  </Th>
                                  <Th 
                                    color={textSecondary} 
                                    fontWeight="700" 
                                    fontSize="sm"
                                    textTransform="none"
                                  >
                                    Value
                                  </Th>
                                  <Th 
                                    color={textSecondary} 
                                    fontWeight="700" 
                                    fontSize="sm"
                                    textTransform="none"
                                  >
                                    Type
                                  </Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {botData.options.map(option => (
                                  <Tr key={option.id}>
                                    <Td>
                                      <VStack align="start" spacing={1}>
                                        <Text fontWeight="600" color={textPrimary}>
                                          {option.name}
                                        </Text>
                                        {option.description && (
                                          <Text fontSize="sm" color={textSecondary}>
                                            {option.description}
                                          </Text>
                                        )}
                                      </VStack>
                                    </Td>
                                    <Td>
                                      {option.option_type === 'boolean' ? (
                                        <Badge 
                                          colorScheme={option.value ? "green" : "gray"}
                                          borderRadius="full"
                                          px={3}
                                          py={1}
                                          fontWeight="600"
                                        >
                                          {option.value ? 'Yes' : 'No'}
                                        </Badge>
                                      ) : (
                                        <Text color={textPrimary} fontWeight="500">
                                          {option.value || 'Not configured'}
                                        </Text>
                                      )}
                                    </Td>
                                    <Td>
                                      <Badge 
                                        variant="outline" 
                                        borderRadius="full"
                                        px={3}
                                        py={1}
                                        fontWeight="600"
                                        textTransform="capitalize"
                                      >
                                        {option.option_type}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </Card>
                      )}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>

        {/* Enhanced Connection Configuration Modal */}
        <Modal 
          isOpen={isConnectionModalOpen} 
          onClose={closeConnectionModal} 
          size="lg"
          scrollBehavior="inside"
        >
          <ModalOverlay bg={modalOverlayBg} />
          <ModalContent 
            bg={modalBg} 
            borderRadius="xl"
            borderWidth="1px"
            borderColor={borderColor}
            maxH="80vh"
          >
            <ModalHeader p={6} borderBottomWidth="1px" borderColor={borderColor}>
              <VStack align="start" spacing={2}>
                <Heading size="md" color={textPrimary}>
                  Configure Connection
                </Heading>
                {selectedConnection && (
                  <HStack>
                    <Icon as={FiLink} color="blue.400" />
                    <Text fontSize="sm" color={textSecondary}>
                      {selectedConnection.name}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </ModalHeader>
            <ModalCloseButton />
            
            {selectedConnection && (
              <ModalBody p={6}>
                <VStack spacing={6} align="stretch">
                  {/* Connection Info - Read Only */}
                  <Card 
                    bg={readOnlyBg} 
                    borderRadius="xl" 
                    p={6}
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <VStack align="start" spacing={4}>
                      <Text fontWeight="700" fontSize="lg" color={textPrimary}>
                        Connection Information
                      </Text>
                      <SimpleGrid columns={2} spacing={6} w="full">
                        <Box>
                          <Text fontSize="sm" color={textTertiary} mb={1} fontWeight="600">
                            Connection Name
                          </Text>
                          <Text fontSize="md" color={textPrimary} fontWeight="600">
                            {selectedConnection?.name}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color={textTertiary} mb={1} fontWeight="600">
                            Provider
                          </Text>
                          <Text fontSize="md" color={textPrimary} fontWeight="600">
                            {selectedConnection?.provider_name || selectedConnection?.provider}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </VStack>
                  </Card>

                  {/* Dynamic Credential Configuration */}
                  <Box>
                    <Text fontWeight="700" fontSize="lg" mb={6} color={textPrimary}>
                      Credentials Configuration
                    </Text>
                    {Object.keys(providerTemplate).length > 0 ? (
                      <VStack spacing={4} align="stretch">
                        {Object.entries(providerTemplate).map(([key, defaultValue]) => 
                          renderFormField(key, connectionFormData[key] || '', providerTemplate)
                        )}
                      </VStack>
                    ) : (
                      <Alert 
                        status="warning" 
                        borderRadius="xl"
                        bg={bg}
                        borderColor={borderColor}
                        borderWidth="1px"
                      >
                        <AlertIcon />
                        <Text fontSize="sm" color={textPrimary}>
                          No configuration template available for this provider type.
                        </Text>
                      </Alert>
                    )}
                  </Box>

                  <Alert 
                    status="info" 
                    borderRadius="xl"
                    bg={bg}
                    borderColor={borderColor}
                    borderWidth="1px"
                  >
                    <AlertIcon />
                    <Text fontSize="sm" color={textPrimary}>
                      Only fill in the fields required by your provider. Empty fields will be ignored.
                    </Text>
                  </Alert>
                </VStack>
              </ModalBody>
            )}

            <ModalFooter p={6} borderTopWidth="1px" borderColor={borderColor}>
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  borderRadius="lg"
                  onClick={closeConnectionModal}
                  borderColor={borderColor}
                  color={textPrimary}
                  _hover={{ borderColor: 'gray.400' }}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  borderRadius="lg"
                  onClick={handleConnectionSave}
                  isLoading={isLoading}
                  loadingText="Saving..."
                  _hover={{ transform: 'translateY(-1px)' }}
                  transition="all 0.2s"
                >
                  Save Credentials
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default BotDetail;